/**
 * 付费分析（Alice 页面 analytics 的复刻）：复购率、首购队列、付费用户画像、模型偏好，以及分析报告。
 *
 * 背景：数据由后端聚合（订单在支付服务，用量在网关库），结果缓存 6 小时；「重新计算」强制刷新。
 *   口径与「充值订单」页一致：已支付且扣除退款后的实收；时间按北京时间。
 * 设计意图：
 *   - 版式、图表、表格与 Alice 逐块对应；金额从人民币换成美元，糖豆换成花瓣。
 *   - Alice 的「支付入口」「获客来源」需要订单入口与注册来源归因，本仓没有这两类数据，对应区块保留并显示空状态，
 *     不用推测数据填充。
 *   - Alice 的「AI 生成报告与追问」依赖后台 Agent，本仓未建；报告区保留列表与详情，生成按钮换成「导入报告」。
 * 关键约束：重新计算要全量扫描，请求期间按钮禁用；导入报告要 admin 角色（后端校验），内容按 Markdown 子集渲染，
 *   不执行任何脚本。
 */
import { IconRefresh } from '@tabler/icons-react'
import { useEffect, useMemo, useState } from 'react'
import { bizApi, type AnalyticsReport, type PayerAnalytics } from '@ap/api-client/admin-biz'
import { UNITS_PER_USD, fmtInt } from '@ap/api-client/money'
import { A } from '@ap/i18n/admin'
import { BIZ } from '@ap/i18n/admin-biz'
import { errorText } from '@ap/i18n/common'
import { intlTag, useI18n } from '@ap/i18n'
import { Button, Field, Input, Modal, Notice, Textarea, fmtDate, useLoad, useToast } from '@ap/ui'
import { ChartCanvas, Markdown, bizError, chartOpts, compactTokens, pctOf } from '../../alice/biz-kit.tsx'
import { OpsNote, OpsPage, Stat, Stats } from '../../alice/kit.tsx'
import { useAdmin } from '../../session.tsx'
import { useUsd } from '../../shared.tsx'

const usdVal = (units: number) => units / UNITS_PER_USD

export function Analytics() {
  const { t, locale } = useI18n()
  const tag = intlTag(locale)
  const usd = useUsd()
  const toast = useToast()
  const { isAdmin } = useAdmin()
  const [refreshing, setRefreshing] = useState(false)
  const [forced, setForced] = useState<PayerAnalytics | null>(null)
  const load = useLoad(() => bizApi.payers(false), [])
  const d: PayerAnalytics | null = forced ?? load.data

  useEffect(() => {
    if (load.error) toast.error(bizError(load.error, locale))
  }, [load.error, toast, locale])

  const recompute = async () => {
    setRefreshing(true)
    try {
      setForced(await bizApi.payers(true))
    } catch (e) {
      console.error('[analytics] 重新计算失败', e)
      toast.error(bizError(e, locale))
    } finally {
      setRefreshing(false)
    }
  }

  const int = (n: number) => fmtInt(n, tag)
  const usd0 = (units: number) => usd(units, 0)
  const s = d?.summary
  const m = d?.matured
  const payerU = d?.usage_overview.payer
  const freeU = d?.usage_overview.free
  const totalCost = (payerU?.cost_units ?? 0) + (freeU?.cost_units ?? 0)

  const charts = useMemo(() => {
    if (!d) return null
    const dist = chartOpts()
    const cohort = chartOpts()
    const monthly = chartOpts()
    monthly.scales.x.stacked = true
    monthly.scales.y.stacked = true
    const models = d.model_preference.slice(0, 10)
    const modelOpts = chartOpts()
    ;(modelOpts as { indexAxis?: string }).indexAxis = 'y'
    modelOpts.plugins.legend = { ...(modelOpts.plugins.legend ?? {}), display: false }
    modelOpts.scales = {
      x: { beginAtZero: true, border: { display: false }, grid: { color: 'rgba(148,163,184,0.12)' }, ticks: { font: { size: 10 }, color: '#9ca3af' } },
      y: { grid: { display: false }, border: { display: false }, ticks: { font: { size: 10 }, color: '#9ca3af' } },
    }
    return {
      dist: {
        type: 'bar' as const,
        data: {
          labels: d.distribution.map((r) => t(BIZ.an.times, { n: r.times })),
          datasets: [{ label: t(BIZ.an.users), data: d.distribution.map((r) => r.users), backgroundColor: 'rgba(59,130,246,0.65)', borderRadius: 4 }],
        },
        options: dist,
      },
      cohort: {
        type: 'bar' as const,
        data: {
          labels: d.weekly_cohorts.map((r) => t(BIZ.an.weekLabel, { d: r.week_start.slice(5) })),
          datasets: [
            { label: t(BIZ.an.cohortUsers), data: d.weekly_cohorts.map((r) => r.users), backgroundColor: 'rgba(148,163,184,0.5)', borderRadius: 4 },
            { label: t(BIZ.an.cohortRepurchased), data: d.weekly_cohorts.map((r) => r.repurchased), backgroundColor: 'rgba(16,185,129,0.7)', borderRadius: 4 },
          ],
        },
        options: cohort,
      },
      monthly: {
        type: 'bar' as const,
        data: {
          labels: d.monthly.map((r) => r.month),
          datasets: [
            { label: t(BIZ.an.newRevenue), data: d.monthly.map((r) => usdVal(r.revenue_units - r.returning_revenue_units)), backgroundColor: 'rgba(59,130,246,0.65)', borderRadius: 4 },
            { label: t(BIZ.an.returningRevenue), data: d.monthly.map((r) => usdVal(r.returning_revenue_units)), backgroundColor: 'rgba(16,185,129,0.7)', borderRadius: 4 },
          ],
        },
        options: monthly,
      },
      models: {
        type: 'bar' as const,
        data: {
          labels: models.map((r) => r.model_id),
          datasets: [
            {
              label: t(BIZ.an.spendUsd),
              data: models.map((r) => Number(usdVal(r.cost_units).toFixed(2))),
              backgroundColor: models.map((r) => (r.kind === 'image' ? 'rgba(139,92,246,0.65)' : 'rgba(59,130,246,0.65)')),
              borderRadius: 4,
            },
          ],
        },
        options: modelOpts,
      },
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d, locale])

  const single = d?.repurchaser_vs_single.find((r) => !r.is_repurchaser)
  const rep = d?.repurchaser_vs_single.find((r) => r.is_repurchaser)
  const ratio = (a?: number, b?: number) => (b && b > 0 && a !== undefined ? `${(a / b).toFixed(1)}x` : '—')
  const compare: [string, string, string, string][] = [
    [t(BIZ.an.cmpUsers), int(single?.users ?? 0), int(rep?.users ?? 0), '—'],
    [t(BIZ.an.cmpPay), usd0(single?.avg_pay_units ?? 0), usd0(rep?.avg_pay_units ?? 0), ratio(rep?.avg_pay_units, single?.avg_pay_units)],
    [t(BIZ.an.cmpCost), usd(single?.avg_cost_units ?? 0), usd(rep?.avg_cost_units ?? 0), ratio(rep?.avg_cost_units, single?.avg_cost_units)],
    [t(BIZ.an.cmpTokens), String(single?.avg_m_tokens ?? 0), String(rep?.avg_m_tokens ?? 0), ratio(rep?.avg_m_tokens, single?.avg_m_tokens)],
    [t(BIZ.an.cmpImages), String(single?.avg_images ?? 0), String(rep?.avg_images ?? 0), ratio(rep?.avg_images, single?.avg_images)],
    [t(BIZ.an.cmpDays), String(single?.avg_active_days || '—'), String(rep?.avg_active_days || '—'), ratio(rep?.avg_active_days, single?.avg_active_days)],
  ]

  const ts = d ? d.generated_at.replace('T', ' ').slice(5, 16) : ''

  return (
    <OpsPage
      eyebrow="PAYER INSIGHTS"
      title={t(A.nav.analytics)}
      desc={t(BIZ.an.desc)}
      actions={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span className="ops-head-chip">{d ? t(d.from_cache ? BIZ.an.cached : BIZ.an.recomputed, { t: ts }) : t(BIZ.common.loading)}</span>
          <button className="btn btn-primary btn-sm" disabled={refreshing} onClick={recompute}>
            <IconRefresh size={13} /> {t(refreshing ? BIZ.an.computing : BIZ.an.recompute)}
          </button>
        </div>
      }
    >
      <OpsNote>{t(BIZ.an.note)}</OpsNote>
      {d?.payments_error && <Notice tone="danger">{t(BIZ.common.paymentsDown)}</Notice>}
      {!!load.error && !d && <Notice tone="danger">{errorText(load.error, locale)}</Notice>}

      <Stats>
        <Stat label={t(BIZ.an.kPayers)} value={s ? int(s.payers) : '—'} sub={s ? t(BIZ.an.kOrders, { n: int(s.orders), v: usd0(s.revenue_units) }) : '—'} />
        <Stat label={t(BIZ.an.kRate)} value={s ? pctOf(s.repurchasers, s.payers) : '—'} color="success" sub={s ? t(BIZ.an.kRepurchasers, { n: int(s.repurchasers) }) : '—'} />
        <Stat
          label={t(BIZ.an.kRevShare)}
          value={s ? pctOf(s.repurchaser_revenue_units, s.revenue_units) : '—'}
          sub={s ? t(BIZ.an.kRev, { v: usd0(s.repurchaser_revenue_units) }) : '—'}
        />
        <Stat
          label={t(BIZ.an.kInterval)}
          value={s && s.avg_interval_days !== null ? t(BIZ.an.days, { n: s.avg_interval_days }) : '—'}
          sub={s && s.median_interval_days !== null ? t(BIZ.an.median, { n: s.median_interval_days }) : t(BIZ.an.noSample)}
        />
      </Stats>
      <Stats>
        <Stat label={t(BIZ.an.kM21)} value={m ? pctOf(m.matured21_repurchasers, m.matured21_users) : '—'} sub={m ? t(BIZ.an.kM21Sub, { a: int(m.matured21_repurchasers), b: int(m.matured21_users) }) : '—'} />
        <Stat label={t(BIZ.an.kM14)} value={m ? pctOf(m.matured14_repurchasers, m.matured14_users) : '—'} sub={m ? t(BIZ.an.kM14Sub, { p: pctOf(m.matured14_repurchase_in14d, m.matured14_users) }) : '—'} />
        <Stat
          label={t(BIZ.an.kTokens)}
          value={payerU ? compactTokens(payerU.tokens) : '—'}
          sub={payerU ? t(BIZ.an.kRequests, { n: int(payerU.requests), v: usd(payerU.cost_units) }) : '—'}
        />
        <Stat
          label={t(BIZ.an.kImages)}
          value={payerU ? t(BIZ.an.imagesUnit, { n: int(payerU.image_requests) }) : '—'}
          sub={payerU ? t(BIZ.an.kUsageShare, { p: pctOf(payerU.cost_units, totalCost) }) : '—'}
        />
      </Stats>

      <div className="dashboard-chart-grid">
        <div className="dashboard-chart-card">
          <div className="chart-card-head">
            <span>{t(BIZ.an.cDist)}</span>
            <small>{t(BIZ.an.cDistSub)}</small>
          </div>
          <div className="chart-h-220">{charts && <ChartCanvas config={charts.dist} label={t(BIZ.an.cDist)} />}</div>
        </div>
        <div className="dashboard-chart-card">
          <div className="chart-card-head">
            <span>{t(BIZ.an.cCohort)}</span>
            <small>{t(BIZ.an.cCohortSub)}</small>
          </div>
          <div className="chart-h-220">{charts && <ChartCanvas config={charts.cohort} label={t(BIZ.an.cCohort)} />}</div>
        </div>
        <div className="dashboard-chart-card">
          <div className="chart-card-head">
            <span>{t(BIZ.an.cMonthly)}</span>
            <small>{t(BIZ.an.cMonthlySub)}</small>
          </div>
          <div className="chart-h-220">{charts && <ChartCanvas config={charts.monthly} label={t(BIZ.an.cMonthly)} />}</div>
        </div>
        <div className="dashboard-chart-card">
          <div className="chart-card-head">
            <span>{t(BIZ.an.cModels)}</span>
            <small>{t(BIZ.an.cModelsSub)}</small>
          </div>
          <div className="chart-h-220">{charts && <ChartCanvas config={charts.models} label={t(BIZ.an.cModels)} />}</div>
        </div>
      </div>

      <div style={{ fontSize: 12, fontWeight: 650, margin: '20px 0 8px' }}>
        {t(BIZ.an.channelTitle)} <span style={{ fontWeight: 500, color: 'var(--text-tertiary)' }}>{t(BIZ.an.channelSub)}</span>
      </div>
      <OpsNote>{t(BIZ.an.channelNote)}</OpsNote>
      <div className="dashboard-chart-grid">
        <div className="dashboard-chart-card">
          <div className="chart-card-head">
            <span>{t(BIZ.an.entryTitle)}</span>
            <small>{t(BIZ.an.entrySub)}</small>
          </div>
          <div className="table-wrap">
            <div className="table-inner">
              <table>
                <thead>
                  <tr>
                    <th>{t(BIZ.an.colEntry)}</th>
                    <th>{t(BIZ.an.colNet)}</th>
                    <th>{t(BIZ.an.colShare)}</th>
                    <th>{t(BIZ.an.colOrders)}</th>
                    <th>{t(BIZ.an.colPayers)}</th>
                    <th>{t(BIZ.an.colSince)}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td colSpan={6} className="empty">
                      {t(BIZ.an.noChannelData)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="dashboard-chart-card">
          <div className="chart-card-head">
            <span>{t(BIZ.an.acqTitle)}</span>
            <small>{t(BIZ.an.acqSub)}</small>
          </div>
          <div className="table-wrap">
            <div className="table-inner">
              <table>
                <thead>
                  <tr>
                    <th>{t(BIZ.an.colSource)}</th>
                    <th>{t(BIZ.an.colNet)}</th>
                    <th>{t(BIZ.an.colShare)}</th>
                    <th>{t(BIZ.an.colPayers)}</th>
                    <th>{t(BIZ.an.colRate)}</th>
                    <th>{t(BIZ.an.colAvgPay)}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td colSpan={6} className="empty">
                      {t(BIZ.an.noChannelData)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <div className="dashboard-chart-card" style={{ marginBottom: 16 }}>
        <div className="chart-card-head">
          <span>{t(BIZ.an.entryMonthly)}</span>
          <small>{t(BIZ.an.entryMonthlySub)}</small>
        </div>
        <div className="empty">{t(BIZ.an.noChannelData)}</div>
      </div>

      <div style={{ fontSize: 12, fontWeight: 650, margin: '20px 0 8px' }}>
        {t(BIZ.an.segTitle)} <span style={{ fontWeight: 500, color: 'var(--text-tertiary)' }}>{t(BIZ.an.segSub)}</span>
      </div>
      <div className="table-wrap">
        <div className="table-inner">
          <table>
            <thead>
              <tr>
                <th>{t(BIZ.an.colSegment)}</th>
                <th>{t(BIZ.an.colUsers)}</th>
                <th>{t(BIZ.an.colContribution)}</th>
                <th>{t(BIZ.an.colRepurchasers)}</th>
                <th>{t(BIZ.an.colRate)}</th>
                <th>{t(BIZ.an.colAvgSpend)}</th>
              </tr>
            </thead>
            <tbody>
              {!d || d.segments.length === 0 ? (
                <tr>
                  <td colSpan={6} className="empty">
                    {d ? t(BIZ.common.noData) : t(BIZ.common.loading)}
                  </td>
                </tr>
              ) : (
                d.segments.map((r) => (
                  <tr key={r.segment}>
                    <td>{BIZ.an.seg[r.segment as keyof typeof BIZ.an.seg] ? t(BIZ.an.seg[r.segment as keyof typeof BIZ.an.seg]) : r.segment}</td>
                    <td>{int(r.users)}</td>
                    <td>{usd0(r.pay_units)}</td>
                    <td>{int(r.repurchasers)}</td>
                    <td>{pctOf(r.repurchasers, r.users)}</td>
                    <td>{usd(r.avg_cost_units)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ fontSize: 12, fontWeight: 650, margin: '20px 0 8px' }}>
        {t(BIZ.an.cmpTitle)} <span style={{ fontWeight: 500, color: 'var(--text-tertiary)' }}>{t(BIZ.an.cmpSub)}</span>
      </div>
      <div className="table-wrap">
        <div className="table-inner">
          <table>
            <thead>
              <tr>
                <th>{t(BIZ.an.cmpMetric)}</th>
                <th>{t(BIZ.an.cmpSingle)}</th>
                <th>{t(BIZ.an.cmpRepeat)}</th>
                <th>{t(BIZ.an.cmpMultiple)}</th>
              </tr>
            </thead>
            <tbody>
              {compare.map((r) => (
                <tr key={r[0]}>
                  <td>{r[0]}</td>
                  <td>{r[1]}</td>
                  <td>{r[2]}</td>
                  <td>{r[3]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ fontSize: 12, fontWeight: 650, margin: '20px 0 8px' }}>
        {t(BIZ.an.topTitle)} <span style={{ fontWeight: 500, color: 'var(--text-tertiary)' }}>{t(BIZ.an.topSub)}</span>
      </div>
      <div className="table-wrap">
        <div className="table-inner">
          <table>
            <thead>
              <tr>
                <th>{t(BIZ.an.colOrg)}</th>
                <th>{t(BIZ.an.colFirstPaid)}</th>
                <th>{t(BIZ.an.colTimes)}</th>
                <th>{t(BIZ.an.colRecharge)}</th>
                <th>{t(BIZ.an.colSpend)}</th>
                <th>Token</th>
                <th>{t(BIZ.an.colImages)}</th>
                <th>{t(BIZ.an.colActive)}</th>
                <th>{t(BIZ.an.colTopModel)}</th>
              </tr>
            </thead>
            <tbody>
              {!d || d.top_payers.length === 0 ? (
                <tr>
                  <td colSpan={9} className="empty">
                    {d ? t(BIZ.common.noData) : t(BIZ.common.loading)}
                  </td>
                </tr>
              ) : (
                d.top_payers.map((r) => (
                  <tr key={r.account_id}>
                    <td>
                      <strong>{r.name}</strong> <span style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>ID {r.account_id.slice(0, 8)}</span>
                    </td>
                    <td>{r.first_paid || '—'}</td>
                    <td>{int(r.pay_n)}</td>
                    <td>
                      <strong>{usd0(r.pay_units)}</strong>
                    </td>
                    <td>{usd(r.cost_units)}</td>
                    <td>{r.m_tokens}M</td>
                    <td>{int(r.images)}</td>
                    <td>{int(r.active_days)}</td>
                    <td className="mono" style={{ fontSize: 11 }}>
                      {r.top_model || '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Reports canImport={isAdmin} />
    </OpsPage>
  )
}

/** 分析报告：列表与详情，来源只有「导入」（Alice 的 AI 生成与追问依赖后台 Agent，本仓未建）。 */
function Reports({ canImport }: { canImport: boolean }) {
  const { t, locale } = useI18n()
  const toast = useToast()
  const list = useLoad(() => bizApi.reports(30), [])
  const [openId, setOpenId] = useState<number | null>(null)
  const detail = useLoad<AnalyticsReport | null>(() => (openId === null ? Promise.resolve(null) : bizApi.report(openId)), [openId])
  const [dialog, setDialog] = useState(false)
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (list.error) toast.error(bizError(list.error, locale))
  }, [list.error, toast, locale])

  const submit = async () => {
    setBusy(true)
    try {
      const r = await bizApi.importReport(title.trim(), content.trim())
      toast.ok(t(BIZ.an.repImported))
      setDialog(false)
      setTitle('')
      setContent('')
      list.reload()
      setOpenId(r.id)
    } catch (e) {
      console.error('[analytics] 导入报告失败', e)
      toast.error(bizError(e, locale))
    } finally {
      setBusy(false)
    }
  }

  const reports = list.data?.reports ?? []
  const r = detail.data
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '28px 0 8px' }}>
        <div style={{ fontSize: 12, fontWeight: 650 }}>
          {t(BIZ.an.repTitle)} <span style={{ fontWeight: 500, color: 'var(--text-tertiary)' }}>{t(BIZ.an.repSub)}</span>
        </div>
        <button className="btn btn-primary btn-sm" disabled={!canImport} onClick={() => setDialog(true)} title={canImport ? undefined : t(A.role.readOnly)}>
          {t(BIZ.an.repImport)}
        </button>
      </div>
      <OpsNote>{t(BIZ.an.repNote)}</OpsNote>
      <div style={{ marginBottom: 10 }}>
        {list.loading && !list.data ? (
          <div style={{ padding: 14, fontSize: 12, color: 'var(--text-tertiary)' }}>{t(BIZ.common.loading)}</div>
        ) : reports.length === 0 ? (
          <div style={{ padding: 14, fontSize: 12, color: 'var(--text-tertiary)' }}>{t(BIZ.an.repEmpty)}</div>
        ) : (
          reports.map((x) => (
            <div key={x.id} className={`an-rep-row${x.id === openId ? ' active' : ''}`} onClick={() => setOpenId(x.id)}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{x.title || t(BIZ.an.repNo, { n: x.id })}</div>
                <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>
                  {fmtDate(x.created_at, locale)} · {x.created_by ? x.created_by.slice(0, 8) : '—'}
                  {x.model_id ? ` · ${x.model_id}` : ''}
                </div>
              </div>
              <span className={`an-rep-badge ${x.source}`}>{t(x.source === 'agent' ? BIZ.an.repAgent : BIZ.an.repImport2)}</span>
            </div>
          ))
        )}
      </div>
      {openId !== null && (
        <div className="an-rep-detail-card">
          {!r ? (
            <div style={{ padding: 14, fontSize: 12, color: 'var(--text-tertiary)' }}>{detail.error ? errorText(detail.error, locale) : t(BIZ.common.loading)}</div>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 6 }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700 }}>{r.title || t(BIZ.an.repNo, { n: r.id })}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 3 }}>
                    {fmtDate(r.created_at, locale)} · {r.created_by ? r.created_by.slice(0, 8) : '—'}{' '}
                    <span className={`an-rep-badge ${r.source}`}>{t(r.source === 'agent' ? BIZ.an.repAgent : BIZ.an.repImport2)}</span>
                  </div>
                </div>
                <button className="btn btn-secondary btn-sm" onClick={() => setOpenId(null)}>
                  {t(BIZ.an.repCollapse)}
                </button>
              </div>
              <Markdown text={r.content ?? ''} />
            </>
          )}
        </div>
      )}

      <Modal
        open={dialog}
        onClose={() => setDialog(false)}
        title={t(BIZ.an.repImport)}
        busy={busy}
        wide
        footer={
          <>
            <Button variant="secondary" disabled={busy} onClick={() => setDialog(false)}>
              {t(BIZ.common.cancel)}
            </Button>
            <Button variant="primary" loading={busy} disabled={!title.trim() || !content.trim()} onClick={submit}>
              {t(BIZ.an.repImportBtn)}
            </Button>
          </>
        }
      >
        <Field label={t(BIZ.an.repFieldTitle)}>
          <Input value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} />
        </Field>
        <Field label={t(BIZ.an.repFieldContent)} hint={t(BIZ.an.repFieldHint)}>
          <Textarea rows={14} value={content} onChange={(e) => setContent(e.target.value)} />
        </Field>
      </Modal>
    </>
  )
}
